import cn from 'clsx';
import { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { ButtonHTMLAttributes, forwardRef, Ref } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * `primary` is the brand's colour and the default, and it is a name rather than a
   * shade for that reason: it resolves to --v2-primary, so the same button is
   * DiscountMags red on one site and TopMags red on another. It replaced `red-600`,
   * which was Tailwind's red and could not follow a brand -- the page it was most
   * visible on rendered its main call to action in #DC2626 while everything else
   * around it was the brand's #CF373C.
   *
   * The others stay literal, and should: yellow and green mean warning and success
   * rather than "this brand", and there is no second value for a brand to swap in.
   */
  color?: 'primary' | 'yellow-400' | 'green-600' | 'secondary' | 'apple-pay';
  shape?: 'pill' | 'rounded' | 'square';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  loadingText?: string;
  className?: string;
  icon?: IconLookup;
  iconAfter?: IconLookup;
  disabled?: boolean;
  ariaLabel?: string;
  /** Fills its container. For form submits and the mobile sticky CTA. */
  fullWidth?: boolean;
};

function Button(
  {
    color = 'primary',
    shape = 'pill',
    size = 'md',
    onClick,
    isLoading = false,
    loadingText = 'Loading...',
    className,
    icon,
    iconAfter,
    disabled = false,
    fullWidth = false,
    children,
    ariaLabel,
    ...props
  }: ButtonProps,
  ref: Ref<HTMLButtonElement>
) {
  const coreClasses = cn('disabled:opacity-50 focus:ring-4');

  /**
   * The focus ring follows the button rather than being one colour for all of them.
   * It used to be `focus:ring-red-200` in coreClasses, which put a red ring on the
   * green and the yellow button too, and -- more to the point here -- a fixed red one
   * on the brand button, where it now themes with the rest of it.
   */
  const ringClasses = {
    primary: 'focus:ring-primary-200',
    'yellow-400': 'focus:ring-yellow-200',
    'green-600': 'focus:ring-green-200',
    secondary: 'focus:ring-slate-200',
    'apple-pay': 'focus:ring-slate-300',
  }[color];

  const shapeClasses = {
    pill: 'rounded-full',
    rounded: 'rounded-md',
    square: null,
  }[shape];

  const textColorClass = {
    primary: 'text-white',
    'yellow-400': 'text-black',
    'green-600': 'text-white',
    secondary: 'text-slate-900 hover:text-white',
    'apple-pay': 'text-white',
  }[color];

  const textSizeClass = {
    xs: 'text-sm',
    sm: 'text-md',
    md: 'text-lg',
    lg: 'text-md xs:text-lg lg:text-xl',
  }[size];

  const textClasses = `${textColorClass} ${textSizeClass} font-semibold`;
  const paddingClasses = {
    xs: 'py-1 px-5',
    sm: 'py-2 px-5',
    md: 'py-2 px-8',
    lg: 'py-2 lg:py-3 px-6 lg:px-10',
  }[size];

  const backgroundClasses = {
    primary: 'bg-primary hover:bg-primary-700',
    'yellow-400': 'bg-yellow-400 hover:bg-yellow-500',
    'green-600': 'bg-green-600 hover:bg-green-700',
    secondary: 'bg-white hover:bg-slate-900 active:bg-black',
    'apple-pay': 'bg-black',
  }[color];

  const borderClasses = {
    primary: 'border-2 border-primary hover:border-primary-700',
    'yellow-400': 'border-2 border-yellow-400 hover:border-yellow-500',
    'green-600': 'border-2 border-green-600 hover:border-green-700',
    secondary: 'border-2 border-slate-900 active:border-black',
    'apple-pay': 'border-2 border-black',
  }[color];

  return (
    <button
      ref={ref}
      className={cn(
        coreClasses,
        ringClasses,
        textClasses,
        paddingClasses,
        backgroundClasses,
        borderClasses,
        shapeClasses,
        fullWidth && 'w-full',
        className
      )}
      onClick={onClick}
      role="button"
      aria-label={ariaLabel}
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading && <FontAwesomeIcon icon={faSpinnerThird} spin className="mr-2" />}
      {!!icon && !isLoading && <FontAwesomeIcon icon={icon} className="mr-2" />}
      <span>{isLoading ? loadingText : children}</span>
      {!!iconAfter && !isLoading && <FontAwesomeIcon icon={iconAfter} className="ml-2" />}
    </button>
  );
}

export default forwardRef(Button);
